import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardMedia,
  CardContent,
  Box,
  IconButton,
  Chip
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import {
  useDeleteEntryMutation,
  useEntryImageUrl,
  useUpdateEntryMutation
} from '../../../hooks/useFirebaseQueries.js';
import { uploadBytes, ref as storageRef } from 'firebase/storage';
import { storage } from '../../../plugins/firebase.js';
import { useDispatch, useSelector } from 'react-redux';
import { waitForDeleteConfirmation } from '../../../store/slices/notificationsSlice.js';
import {
  BirdInfoSection,
  QuickActionsBar,
  WeightDialog,
  EventDialog
} from './index.js';
import { selectUserUid, selectUserInfo } from '../../../store/slices/userSlice.js';

const BirdCard = ({ entry }) => {
  // Navigation and state management
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userUid = useSelector(selectUserUid);
  const user = useSelector(selectUserInfo);

  // Local state for quick weight entry dialog
  const [weightDialogOpen, setWeightDialogOpen] = useState(false);

  // Local state for event entry dialog
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [newEvent, setNewEvent] = useState('');
  const [eventDate, setEventDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  // Firebase mutations for entry operations
  const deleteEntryMutation = useDeleteEntryMutation();
  const updateEntryMutation = useUpdateEntryMutation();

  // Get entry image URL from Firebase storage
  const { data: imageUrl } = useEntryImageUrl(entry);

  console.log('imageUrl', imageUrl);

  // Utility functions for bird terminology and data formatting

  /**
   * Determines proper gender terminology based on sex and age
   * @param {string} sex - 'male' or 'female'
   * @param {number} ageInYears - Age of the bird in years
   * @returns {string} - 'Pullet', 'Hen', 'Cockerel', 'Rooster', or empty string
   */
  const getGenderTerm = (sex, ageInYears) => {
    if (!sex || sex.toLowerCase() === 'unknown') return '';
    if (sex.toLowerCase() === 'female') {
      return ageInYears < 1 ? 'Pullet' : 'Hen';
    } else if (sex.toLowerCase() === 'male') {
      return ageInYears < 1 ? 'Cockerel' : 'Rooster';
    }
    return '';
  };

  /**
   * Calculates age in years from hatch date
   * @param {string} hatchDate - ISO date string of when bird hatched
   * @returns {number|null} - Age in years or null if no hatch date
   */
  const calculateAge = (hatchDate) => {
    if (!hatchDate) return null;
    const now = new Date();
    const hatched = new Date(hatchDate);
    const diffInMs = now - hatched;
    const ageInYears = diffInMs / (1000 * 60 * 60 * 24 * 365.25);
    return ageInYears;
  };

  /**
   * Formats age for display (days, weeks, months, or years)
   * @param {number|null} ageInYears - Age in years
   * @returns {string} - Formatted age string like "5d", "8w", "3m", or "2y"
   */
  const formatAge = (ageInYears) => {
    if (ageInYears === null) return '';

    if (ageInYears < 1) {
      const days = Math.floor(ageInYears * 365.25);
      const weeks = Math.floor(ageInYears * 52.17);
      const months = Math.floor(ageInYears * 12);

      // Show days for very young birds (under 2 weeks)
      if (days < 14) {
        return `${days}d`;
      }
      // Show weeks for birds under 1 year but over 2 weeks
      if (months === 0 || weeks < 12) {
        return `${weeks}w`;
      }
      // Show months for older juveniles
      return `${months}m`;
    }
    return `${Math.floor(ageInYears)}y`;
  };

  /**
   * Gets the most recent weight entry and formats it for display
   * @returns {string|null} - Formatted weight string or null if no weights
   */
  const getCurrentWeight = () => {
    if (!entry?.weights || entry.weights.length === 0) return null;
    // Sort weights by date to get the most recent
    const sortedWeights = [...entry.weights].sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );
    const latestWeight = sortedWeights[0];

    // Convert from grams to lbs for display (standardized storage format is grams)
    const weightInLbs = latestWeight.weightGrams
      ? (latestWeight.weightGrams / 453.592).toFixed(2)
      : latestWeight.originalWeight?.toFixed(2) || '0';

    return `${weightInLbs} lbs`;
  };

  /**
   * Formats variety information for display, showing "Mix" with tooltip for multiple varieties
   * Variety: Color/pattern subdivision within a breed (e.g., "Buff", "Silver Laced", "White")
   * @returns {object} - Object with displayText and tooltipText
   */
  const getVarietyDisplay = () => {
    if (!entry.variety) return { displayText: '', tooltipText: '' };

    // Handle case where variety is an array of multiple varieties
    if (Array.isArray(entry.variety)) {
      if (entry.variety.length === 0)
        return { displayText: '', tooltipText: '' };

      // Extract labels from objects or use strings directly
      const varietyLabels = entry.variety
        .map((v) => (typeof v === 'object' && v.label ? v.label : v))
        .filter(Boolean);

      if (varietyLabels.length === 0)
        return { displayText: '', tooltipText: '' };
      if (varietyLabels.length === 1)
        return { displayText: varietyLabels[0], tooltipText: '' };
      return {
        displayText: 'Mix',
        tooltipText: varietyLabels.join(', ')
      };
    }

    // Handle case where variety is a string with comma-separated values
    if (typeof entry.variety === 'string') {
      const varieties = entry.variety
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v.length > 0);
      if (varieties.length === 0) return { displayText: '', tooltipText: '' };
      if (varieties.length === 1)
        return { displayText: varieties[0], tooltipText: '' };
      return {
        displayText: 'Mix',
        tooltipText: varieties.join(', ')
      };
    }

    // Handle single object case
    if (typeof entry.variety === 'object' && entry.variety.label) {
      return { displayText: entry.variety.label, tooltipText: '' };
    }

    return { displayText: entry.variety.toString(), tooltipText: '' };
  };

  // Calculate display values for the bird
  const ageInYears = calculateAge(entry.hatchDate);
  const genderTerm = getGenderTerm(entry.sex, ageInYears || 0);
  const ageDisplay = formatAge(ageInYears);
  const currentWeight = getCurrentWeight();
  const varietyDisplay = getVarietyDisplay();

  // Event handlers for card actions

  /**
   * Handles entry deletion with confirmation dialog
   * @param {Event} e - Click event
   */
  const handleDelete = async (e) => {
    e.stopPropagation(); // Prevent card navigation
    const confirmed = await dispatch(waitForDeleteConfirmation());
    if (confirmed) {
      deleteEntryMutation.mutate(entry.entryId);
    }
  };

  /**
   * Navigates to the entry details page for editing
   * @param {Event} e - Click event
   */
  const handleEdit = (e) => {
    e.stopPropagation(); // Prevent double navigation
    navigate(`/entry/${entry.entryId}`);
  };

  /**
   * Opens the quick weight entry dialog
   * @param {Event} e - Click event
   */
  const handleQuickAddWeight = (e) => {
    e.stopPropagation();
    setWeightDialogOpen(true);
  };

  /**
   * Opens the quick event entry dialog
   * @param {Event} e - Click event
   */
  const handleQuickAddEvent = (e) => {
    e.stopPropagation();
    setEventDialogOpen(true);
  };

  /**
   * Handles photo upload for the entry
   * @param {Event} e - File input change event
   */
  const handlePhotoUpload = async (e) => {
    e.stopPropagation();
    const file = e.target.files?.[0];
    if (!file) return;
    console.log('updload entry', entry);
    console.log('selectUserUid', selectUserUid);

    try {
      // Generate new photo ID
      const newPhotoId = crypto.randomUUID();

      // Upload the file to Firebase Storage
      const refPath = storageRef(
        storage,
        `${userUid}/${entry.entryId}/${newPhotoId}.jpg`
      );
      await uploadBytes(refPath, file);
      console.log('refPath', refPath);

      console.log('Photo uploaded successfully');
    } catch (error) {
      console.error('Error uploading photo:', error);
    }
  };

  /**
   * Handles recording an egg laid today
   * @param {Event} e - Click event
   */

  /**
   * Handles submission of new event entry
   */
  const handleEventSubmit = async () => {
    if (!newEvent.trim()) return;

    try {
      const eventEntry = {
        date: eventDate, // User-selected date
        timestamp: new Date(),
        description: newEvent.trim(),
        recordedBy: user?.displayName || 'Unknown'
      };

      // Add new event to existing events
      const currentEvents = entry?.events || [];
      const updatedEvents = [...currentEvents, eventEntry];

      // Update the entry in Firebase
      await updateEntryMutation.mutateAsync({
        entryId: entry.entryId,
        entryData: {
          ...entry,
          events: updatedEvents
        },
        userDisplayName: user?.displayName,
        userPhotoURL: user?.photoURL
      });

      // Reset form and close dialog
      setNewEvent('');
      setEventDate(new Date().toISOString().split('T')[0]); // Reset to today
      setEventDialogOpen(false);
    } catch (error) {
      console.error('Error adding event:', error);
    }
  };

  /**
   * Handles cancellation of event entry dialog
   */
  const handleEventCancel = () => {
    setNewEvent('');
    setEventDate(new Date().toISOString().split('T')[0]); // Reset to today
    setEventDialogOpen(false);
  };

  /**
   * Handles clicking on the card to navigate to entry details
   */
  const handleCardClick = () => {
    navigate(`/entry/${entry.entryId}`);
  };

  return (
    <Card
      elevation={0}
      sx={{
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        height: 420,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
          borderColor: 'primary.main'
        }
      }}
      onClick={handleCardClick}
    >
      {/* Image section with overlay content */}
      <Box sx={{ position: 'relative', height: 220 }}>
        <CardMedia
          component="img"
          height="220"
          image={imageUrl || 'https://cdn.vuetifyjs.com/images/cards/docks.jpg'}
          alt={`${entry.name || 'Entry'} ${entry.entryId}`}
          sx={{
            objectFit: 'cover',
            width: '100%',
            height: '100%'
          }}
        />

        {/* Gradient overlay for better text readability */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '50%',
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, transparent 100%)',
            zIndex: 1
          }}
        />

        {/* Status badges in top left */}
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            zIndex: 2,
            display: 'flex',
            gap: 0.5
          }}
        >
          {entry.isFavorited && (
            <Chip
              label="★"
              size="small"
              sx={{
                minWidth: 'auto',
                height: 28,
                fontSize: '0.75rem',
                fontWeight: 'bold',
                backgroundColor: 'rgba(255, 193, 7, 0.95)',
                color: 'white',
                border: 'none',
                backdropFilter: 'blur(4px)'
              }}
            />
          )}
          {entry.isFoundation && (
            <Chip
              label="F"
              size="small"
              sx={{
                minWidth: 'auto',
                height: 28,
                fontSize: '0.75rem',
                fontWeight: 'bold',
                backgroundColor: 'rgba(25, 118, 210, 0.95)',
                color: 'white',
                border: 'none',
                backdropFilter: 'blur(4px)'
              }}
            />
          )}
        </Box>

        {/* Action buttons in top right */}
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 2,
            display: 'flex',
            gap: 0.5,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 2,
            p: 0.5,
            backdropFilter: 'blur(4px)'
          }}
        >
          <IconButton
            size="small"
            color="primary"
            onClick={handleEdit}
            sx={{ p: 0.5 }}
          >
            <Edit fontSize="small" />
          </IconButton>

          <IconButton
            size="small"
            color="error"
            onClick={handleDelete}
            sx={{ p: 0.5 }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>

        {/* Hidden file input for photo upload */}
        <input
          id={`photo-upload-${entry.entryId}`}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handlePhotoUpload}
          onClick={(e) => e.stopPropagation()}
          style={{ display: 'none' }}
        />
      </Box>

      {/* Content section */}
      <CardContent
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 2,
          pb: 2
        }}
      >
        <BirdInfoSection
          entry={entry}
          genderTerm={genderTerm}
          ageDisplay={ageDisplay}
          varietyDisplay={varietyDisplay}
          currentWeight={currentWeight}
        />

        <Box sx={{ flexGrow: 1 }} />

        <QuickActionsBar
          entry={entry}
          onEventAdd={handleQuickAddEvent}
          onWeightAdd={handleQuickAddWeight}
        />
      </CardContent>

      <WeightDialog
        weightDialogOpen={weightDialogOpen}
        setWeightDialogOpen={setWeightDialogOpen}
        entry={entry}
      />

      <EventDialog
        open={eventDialogOpen}
        onClose={handleEventCancel}
        onSubmit={handleEventSubmit}
        entry={entry}
        newEvent={newEvent}
        setNewEvent={setNewEvent}
        eventDate={eventDate}
        setEventDate={setEventDate}
        isSubmitting={updateEntryMutation.isPending}
      />
    </Card>
  );
};

export default BirdCard;
